/**
 * Adventure Reward Calculator
 * アドベンチャーモードの報酬計算・付与
 *
 * Created: 2025-12-17
 */

import type {
  AdventureProgress,
  NpcId,
  BattleResult,
  Reward,
  PackReward,
  BattleRewardResult,
} from '../data/types';
import { getNpcConfig, ALL_CLEAR_BONUS } from '../data/constants';
import {
  isFirstBattle,
  canClaimRepeatReward,
  recordRewardClaimed,
  isAllCleared,
  hasAllClearBonus,
} from './progressManager';
import { getNewUnlocksOnClear } from './unlockChecker';
import { addTitle, saveProgress } from '../data/storage';

// ============================================================
// 報酬計算
// ============================================================

/**
 * 初回クリア報酬を取得
 */
export function getFirstClearReward(npcId: NpcId): Reward | null {
  const config = getNpcConfig(npcId);
  if (!config) return null;

  return config.firstClearReward;
}

/**
 * 再戦報酬を計算（ランダム要素あり）
 */
export function calculateRepeatReward(npcId: NpcId): Reward {
  const config = getNpcConfig(npcId);
  if (!config) {
    return { gold: 0, packs: [] };
  }

  const { goldMin, goldMax, packChance, packType } = config.repeatReward;

  // ゴールドをランダムに決定
  const gold = Math.floor(Math.random() * (goldMax - goldMin + 1)) + goldMin;

  // パックドロップ判定
  const packs: PackReward[] = [];
  if (packType && Math.random() < packChance) {
    packs.push({ type: packType, count: 1 });
  }

  return { gold, packs };
}

/**
 * 対戦結果に基づく報酬を計算
 */
export function calculateBattleReward(
  progress: AdventureProgress,
  npcId: NpcId,
  result: BattleResult
): BattleRewardResult {
  // 敗北時は報酬なし
  if (result === 'lose') {
    return {
      gold: 0,
      packs: [],
      newUnlocks: [],
      isFirstClear: false,
    };
  }

  const isFirst = isFirstBattle(progress, npcId);

  // 初回クリア報酬
  if (isFirst) {
    const reward = getFirstClearReward(npcId);
    const newUnlocks = getNewUnlocksOnClear(progress, npcId);

    return {
      gold: reward?.gold || 0,
      packs: reward?.packs || [],
      title: reward?.title,
      newUnlocks,
      isFirstClear: true,
    };
  }

  // 再戦報酬（デイリー制限チェック）
  if (canClaimRepeatReward(progress, npcId)) {
    const reward = calculateRepeatReward(npcId);

    return {
      gold: reward.gold,
      packs: reward.packs,
      newUnlocks: [],
      isFirstClear: false,
    };
  }

  // デイリー制限超過：報酬なし
  return {
    gold: 0,
    packs: [],
    newUnlocks: [],
    isFirstClear: false,
  };
}

// ============================================================
// 報酬付与
// ============================================================

/**
 * 未開封パックを追加するヘルパー
 * playerData.unopenedPacksByType: { normal: number, fire: number, water: number, ... }
 */
function addUnopenedPacks(playerData: any, packs: PackReward[]): any {
  let updated = { ...playerData };

  // unopenedPacksByTypeが存在しない場合は初期化
  if (!updated.unopenedPacksByType) {
    updated.unopenedPacksByType = {
      normal: 0,
      fire: 0,
      water: 0,
      light: 0,
      dark: 0,
      primitive: 0,
      future: 0,
      royal: 0,
    };
  }

  for (const pack of packs) {
    updated.unopenedPacksByType = {
      ...updated.unopenedPacksByType,
      [pack.type]: (updated.unopenedPacksByType[pack.type] || 0) + pack.count,
    };
  }

  // 既存のunopenedPacks（ノーマルパック）との互換性
  // unopenedPacksByType.normalとunopenedPacksを同期
  updated.unopenedPacks = updated.unopenedPacksByType.normal || 0;

  return updated;
}

/**
 * 報酬を付与（playerDataを更新）
 * @param playerData - プレイヤーデータ（magic-spirit.tsxから）
 * @param reward - 付与する報酬
 * @returns 更新されたプレイヤーデータ
 */
export function applyReward(
  playerData: any,
  reward: BattleRewardResult
): any {
  let updated = { ...playerData };

  // ゴールド付与
  if (reward.gold > 0) {
    updated = {
      ...updated,
      gold: (updated.gold || 0) + reward.gold,
      stats: {
        ...updated.stats,
        totalGoldEarned: (updated.stats?.totalGoldEarned || 0) + reward.gold,
      },
      updatedAt: Date.now(),
    };
  }

  // パックを未開封パックとして追加
  if (reward.packs && reward.packs.length > 0) {
    updated = addUnopenedPacks(updated, reward.packs);
  }

  // 称号付与（adventureProgress経由で管理）
  // Note: 称号はadventureProgressで管理するため、ここでは付与しない

  return updated;
}

/**
 * 報酬付与後の進行データ更新
 */
export function updateProgressAfterReward(
  progress: AdventureProgress,
  npcId: NpcId,
  reward: BattleRewardResult
): AdventureProgress {
  let updated = progress;

  // 再戦報酬の場合、報酬獲得回数を記録
  if (!reward.isFirstClear && reward.gold > 0) {
    updated = recordRewardClaimed(updated, npcId);
  }

  return updated;
}

// ============================================================
// 全クリアボーナス
// ============================================================

/**
 * 全クリアボーナスを計算
 */
export function calculateAllClearBonus(
  progress: AdventureProgress
): BattleRewardResult | null {
  // 全クリアしていない
  if (!isAllCleared(progress)) {
    return null;
  }

  // 既にボーナス受取済み
  if (hasAllClearBonus(progress)) {
    return null;
  }

  return {
    gold: ALL_CLEAR_BONUS.gold,
    packs: ALL_CLEAR_BONUS.packs,
    title: ALL_CLEAR_BONUS.title,
    newUnlocks: [],
    isFirstClear: false,
  };
}

/**
 * 全クリアボーナスを付与
 */
export function applyAllClearBonus(
  playerData: any,
  progress: AdventureProgress
): { playerData: any; progress: AdventureProgress } | null {
  const bonus = calculateAllClearBonus(progress);
  if (!bonus) {
    return null;
  }

  // プレイヤーデータにゴールド付与
  const updatedPlayerData = applyReward(playerData, bonus);

  // 進行データに称号追加
  let updatedProgress = addTitle(progress, ALL_CLEAR_BONUS.title);
  saveProgress(updatedProgress);

  return {
    playerData: updatedPlayerData,
    progress: updatedProgress,
  };
}

// ============================================================
// パック開封ヘルパー
// ============================================================

/**
 * パック報酬をカードに変換（packSystemと連携）
 * Note: 実際のカード追加は呼び出し側で行う
 */
export function getPackOpeningInfo(packs: PackReward[]): {
  normalPacks: number;
  attributePacks: { [key: string]: number };
  royalPacks: number;
} {
  const result = {
    normalPacks: 0,
    attributePacks: {} as { [key: string]: number },
    royalPacks: 0,
  };

  for (const pack of packs) {
    if (pack.type === 'normal') {
      result.normalPacks += pack.count;
    } else if (pack.type === 'royal') {
      result.royalPacks += pack.count;
    } else {
      // 属性パック
      result.attributePacks[pack.type] =
        (result.attributePacks[pack.type] || 0) + pack.count;
    }
  }

  return result;
}

// ============================================================
// 報酬表示用ヘルパー
// ============================================================

/**
 * 報酬を表示用文字列に変換
 */
export function formatRewardText(reward: BattleRewardResult): string[] {
  const lines: string[] = [];

  if (reward.gold > 0) {
    lines.push(`💰 ${reward.gold.toLocaleString()}G`);
  }

  for (const pack of reward.packs) {
    const packName = getPackDisplayName(pack.type);
    lines.push(`🎴 ${packName}×${pack.count}`);
  }

  if (reward.title) {
    lines.push(`🏆 称号「${reward.title}」`);
  }

  return lines;
}

/**
 * パックタイプの表示名を取得
 */
function getPackDisplayName(type: PackReward['type']): string {
  const names: Record<PackReward['type'], string> = {
    normal: 'ノーマルパック',
    fire: '炎パック',
    water: '水パック',
    light: '光パック',
    dark: '闇パック',
    primitive: '原始パック',
    future: '未来パック',
    royal: 'ロイヤルパック',
  };
  return names[type];
}

/**
 * 初回クリア報酬のプレビューを取得
 */
export function getFirstClearRewardPreview(npcId: NpcId): string[] {
  const reward = getFirstClearReward(npcId);
  if (!reward) return [];

  const lines: string[] = [];

  lines.push(`💰 ${reward.gold.toLocaleString()}G`);

  for (const pack of reward.packs) {
    const packName = getPackDisplayName(pack.type);
    lines.push(`🎴 ${packName}×${pack.count}`);
  }

  if (reward.title) {
    lines.push(`🏆 称号「${reward.title}」`);
  }

  return lines;
}
